import type { ComponentProps } from 'react'

import { FormItem, NumberInput } from '@monthio/design-system'
import { useField, useFormikContext } from 'formik'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

type FormikNumberInputProps = ComponentProps<typeof NumberInput> & {
	fieldName: string
	label: string
	hintText?: string
	isRequired?: boolean
	additionalOnChange?: (value: number | undefined) => void
}

export const FormikNumberInput = ({
	fieldName,
	label,
	hintText,
	isRequired,
	additionalOnChange,
	onChange,
	value,
	...props
}: FormikNumberInputProps) => {
	const { t } = useTranslation()
	const [field, meta, { setValue, setTouched }] = useField<number | undefined>(
		fieldName,
	)
	const formik = useFormikContext()

	const handleChange = useCallback(
		(value: number | undefined) => {
			if (onChange) {
				// If onChange is supplied it overrides the default formik integration
				onChange(value)
			} else {
				if (value == null || !isNaN(value)) {
					setValue(value)
				} else {
					setValue(undefined)
				}
				// When additionalOnChange is supplied, it is chained after the default formik integration
				additionalOnChange?.(value)
			}

			// Either way we need to wriggle a bit to make the react-aria number input behavior work with formik validation
			setTimeout(() => {
				formik.validateField(fieldName)
			}, 100)
		},
		[additionalOnChange, fieldName, formik, onChange, setValue],
	)

	return (
		<FormItem
			label={label}
			isRequired={isRequired}
			requiredText={t('required')}
			errorText={meta.touched ? meta.error : undefined}
			hintText={hintText}
		>
			<NumberInput
				{...props}
				value={value ?? field.value}
				onChange={handleChange}
				onBlur={(e) => {
					props.onBlur?.(e)
					setTouched(true)
				}}
				defaultValue={undefined}
			/>
		</FormItem>
	)
}
